// src/pages/Dashboard/Admin/Articles/hooks/useArticlesDashboard.js

import { useCallback, useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";

import {
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  activateArticle,
  updateOrderArticle,
} from "../../../../../services/articlesServices";

import { uploadArticlePhoto } from "../../../../../services/articlesPhotoServices";

import {
  buildArticlePayload,
  calculateArticlesStats,
  filterArticles,
  getCategoryOptions,
  getDefaultArticleForm,
  normalizeOrder,
  sortArticlesByOrder,
} from "../utils/articlesDashboardUtils";

export function useArticlesDashboard() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    query: "",
    category: "",
    stockable: "",
    active: "",
  });

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editableArticle, setEditableArticle] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);

  const [imageDrawerOpen, setImageDrawerOpen] = useState(false);
  const [imageArticle, setImageArticle] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getArticles();
      const rows = sortArticlesByOrder(Array.isArray(response) ? response : []);

      setArticles(rows);
    } catch (error) {
      console.error("Erreur chargement articles :", error);
      toast.error("Erreur lors du chargement des articles.");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const categoryOptions = useMemo(() => getCategoryOptions(articles), [articles]);

  const filteredArticles = useMemo(() => {
    return filterArticles(articles, filters);
  }, [articles, filters]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.query.trim() !== "" ||
      filters.category !== "" ||
      filters.stockable !== "" ||
      filters.active !== ""
    );
  }, [filters]);

  const canReorder = !loading && !reordering && !hasActiveFilters;

  const stats = useMemo(() => calculateArticlesStats(articles), [articles]);

  const filteredStats = useMemo(
    () => calculateArticlesStats(filteredArticles),
    [filteredArticles]
  );

  const nextOrder = useMemo(() => {
    const maxOrder = articles.reduce(
      (max, article) => Math.max(max, Number(article?.ordre_article || 0)),
      0
    );

    return maxOrder + 1;
  }, [articles]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
      category: "",
      stockable: "",
      active: "",
    });
  };

  const openCreateDrawer = () => {
    setFormMode("create");
    setEditableArticle(getDefaultArticleForm(nextOrder));
    setFormDrawerOpen(true);
  };

  const openEditDrawer = (article) => {
    setFormMode("edit");
    setEditableArticle(article);
    setFormDrawerOpen(true);
  };

  const closeFormDrawer = () => {
    if (submittingForm) return;

    setFormDrawerOpen(false);
    setEditableArticle(null);
    setFormMode("create");
  };

  const handleCreateArticle = async (formData) => {
    try {
      setSubmittingForm(true);

      const payload = buildArticlePayload(formData);
      await createArticle(payload);

      toast.success("Article ajouté avec succès.");
      await fetchArticles();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur création article :", error);
      toast.error("Erreur lors de l’ajout de l’article.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleUpdateArticle = async (formData) => {
    if (!editableArticle?.id) {
      toast.error("Article introuvable.");
      return;
    }

    try {
      setSubmittingForm(true);

      const payload = buildArticlePayload(formData);
      await updateArticle(editableArticle.id, payload);

      toast.success("Article mis à jour avec succès.");
      await fetchArticles();
      closeFormDrawer();
    } catch (error) {
      console.error("Erreur mise à jour article :", error);
      toast.error("Erreur lors de la mise à jour de l’article.");
    } finally {
      setSubmittingForm(false);
    }
  };

  const handleDeleteArticle = async (article) => {
    if (!article?.id) return;

    if (!window.confirm(`Supprimer l’article « ${article.nom || "sans nom"} » ?`)) {
      return;
    }

    try {
      setDeletingId(article.id);
      await deleteArticle(article.id);

      toast.success("Article supprimé avec succès.");
      await fetchArticles();
    } catch (error) {
      console.error("Erreur suppression article :", error);
      toast.error("Erreur lors de la suppression de l’article.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleActiveStatus = async (article) => {
    if (!article?.id) return;

    try {
      setTogglingId(article.id);
      await activateArticle(article.id);

      setArticles((prev) =>
        prev.map((item) =>
          item.id === article.id
            ? {
                ...item,
                is_active: !item.is_active,
              }
            : item
        )
      );

      toast.success("Visibilité boutique mise à jour.");
      await fetchArticles();
    } catch (error) {
      console.error("Erreur activation article :", error);
      toast.error("Erreur lors de la mise à jour du statut.");
    } finally {
      setTogglingId(null);
    }
  };

  const persistOrder = async (orderedRows) => {
    await updateOrderArticle(
      orderedRows.map((article) => ({
        id: article.id,
        ordre_article: article.ordre_article,
      }))
    );
  };

  const applyReorder = async (newRows) => {
    const orderedRows = normalizeOrder(newRows);

    try {
      setReordering(true);
      setArticles(orderedRows);

      await persistOrder(orderedRows);
      toast.success("Ordre d’affichage mis à jour.");
      await fetchArticles();
    } catch (error) {
      console.error("Erreur ordre articles :", error);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      await fetchArticles();
    } finally {
      setReordering(false);
    }
  };

  const moveArticle = async (articleId, direction) => {
    if (!canReorder) return;

    const rows = sortArticlesByOrder(articles);
    const index = rows.findIndex((article) => article.id === articleId);

    if (index === -1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= rows.length) return;

    const nextRows = [...rows];
    const current = nextRows[index];

    nextRows[index] = nextRows[targetIndex];
    nextRows[targetIndex] = current;

    await applyReorder(nextRows);
  };

  const handleDragStart = (event, article) => {
    if (!canReorder || !article?.id) return;

    setDraggingId(article.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(article.id));
  };

  const handleDragOver = (event, article) => {
    if (!canReorder || !article?.id) return;

    event.preventDefault();
    setDragOverId(article.id);
  };

  const handleDragLeave = () => {
    if (!canReorder) return;
    setDragOverId(null);
  };

  const handleDrop = async (event, targetArticle) => {
    if (!canReorder || !targetArticle?.id) return;

    event.preventDefault();

    const sourceIdRaw = event.dataTransfer.getData("text/plain");
    const sourceId = sourceIdRaw ? Number(sourceIdRaw) : draggingId;
    const targetId = targetArticle.id;

    setDraggingId(null);
    setDragOverId(null);

    if (!sourceId || sourceId === targetId) return;

    const rows = sortArticlesByOrder(articles);
    const fromIndex = rows.findIndex((article) => article.id === sourceId);
    const toIndex = rows.findIndex((article) => article.id === targetId);

    if (fromIndex === -1 || toIndex === -1) return;

    const nextRows = [...rows];
    const [moved] = nextRows.splice(fromIndex, 1);

    nextRows.splice(toIndex, 0, moved);

    await applyReorder(nextRows);
  };

  const openImageDrawer = (article) => {
    setImageArticle(article || null);
    setImageDrawerOpen(true);
  };

  const closeImageDrawer = () => {
    if (uploadingImage) return;

    setImageDrawerOpen(false);
    setImageArticle(null);
  };

  const handleUploadImage = async ({ file, format, onProgress }) => {
    if (!imageArticle?.id) {
      toast.error("Article introuvable.");
      return;
    }

    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    const validImage = /^image\/(png|jpe?g|webp|gif)$/i.test(file.type);

    if (!validImage) {
      toast.error("Format image non supporté. Utilisez PNG, JPG ou WEBP.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop volumineuse. Taille maximale : 10 Mo.");
      return;
    }

    try {
      setUploadingImage(true);

      const updated = await uploadArticlePhoto(imageArticle.id, file, {
        format,
        onUploadProgress: (event) => {
          if (!event.total || typeof onProgress !== "function") return;
          onProgress(Math.round((event.loaded * 100) / event.total));
        },
      });

      setArticles((prev) =>
        prev.map((article) =>
          article.id === imageArticle.id
            ? {
                ...article,
                photo_url: updated?.photo_url || article.photo_url,
              }
            : article
        )
      );

      toast.success("Image mise à jour.");
      await fetchArticles();
      closeImageDrawer();
    } catch (error) {
      console.error("Erreur upload image article :", error);
      toast.error("Erreur lors de l’upload de l’image.");
    } finally {
      setUploadingImage(false);
    }
  };

  return {
    articles,
    filteredArticles,
    loading,

    filters,
    setFilter,
    resetFilters,

    categoryOptions,
    hasActiveFilters,
    canReorder,
    stats,
    filteredStats,
    nextOrder,

    formDrawerOpen,
    formMode,
    editableArticle,
    submittingForm,
    openCreateDrawer,
    openEditDrawer,
    closeFormDrawer,
    handleCreateArticle,
    handleUpdateArticle,

    imageDrawerOpen,
    imageArticle,
    uploadingImage,
    openImageDrawer,
    closeImageDrawer,
    handleUploadImage,

    deletingId,
    togglingId,
    reordering,

    draggingId,
    dragOverId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    moveArticle,

    fetchArticles,
    handleDeleteArticle,
    toggleActiveStatus,
  };
}